/**
 * Reconciles a row's in-progress edit drafts against a freshly-arrived version of
 * that row (e.g. from a SignalR live update or a background refetch).
 *
 * Rule: keep a draft while it still differs from the incoming source value (the
 * user's unsaved edit must survive a live update to the same or another cell);
 * drop it only once the source value matches the draft, which is treated as the
 * user's own save having landed.
 *
 * Returns the SAME object reference when nothing changes, so callers can use it
 * directly in a `setState` updater without triggering an extra render.
 */
export function reconcileDrafts(
  prevDrafts: Record<string, unknown>,
  sourceCells: ReadonlyArray<{ name: string; value: unknown }>
): Record<string, unknown> {
  const keys = Object.keys(prevDrafts);
  if (keys.length === 0) return prevDrafts;

  const sourceByName = new Map(sourceCells.map(c => [c.name, c.value]));

  let changed = false;
  const next: Record<string, unknown> = {};
  for (const key of keys) {
    if (Object.is(sourceByName.get(key), prevDrafts[key])) {
      // The live/source value now equals the draft -> the user's edit has landed.
      changed = true;
      continue;
    }
    next[key] = prevDrafts[key];
  }

  return changed ? next : prevDrafts;
}

/**
 * Detects rows that the user was actively editing (had unsaved drafts) and that
 * have just disappeared from the table in place; e.g. removed by a live SignalR
 * delete while the user had uncommitted edits.
 *
 * Guards against false positives: only reports removals when at least one row
 * persisted across the change. A page change or filter swap replaces the whole
 * set with no overlap, so it is not mistaken for an in-place deletion.
 */
export function detectClobberedDraftRows<ID>(
  prevIds: ReadonlySet<ID>,
  currentIds: ReadonlySet<ID>,
  dirtyIds: ReadonlySet<ID>
): ID[] {
  let hasOverlap = false;
  for (const id of currentIds) {
    if (prevIds.has(id)) { hasOverlap = true; break; }
  }
  if (!hasOverlap) return [];

  const clobbered: ID[] = [];
  for (const id of dirtyIds) {
    if (prevIds.has(id) && !currentIds.has(id)) clobbered.push(id);
  }
  return clobbered;
}
